import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RilaRateSheetStateService, ProtectionType } from '../state/rila-rate-sheet-state.service';
import { SessionContextService } from '../../../core/services/session-context.service';
import { RsPopoverService } from '../../rate-sheet/rail/pickers/rs-popover.service';
import { IndexPickerComponent, INDEX_PICKER_STATE } from '../../rate-sheet/rail/pickers/index-picker.component';
import { RateSheetTilesComponent } from '../../rate-sheet/rail/shared/rate-sheet-tiles.component';
import { RateSheetFiltersComponent } from '../../rate-sheet/rail/shared/rate-sheet-filters.component';
import { RateSheetChangesComponent } from '../../rate-sheet/rail/shared/rate-sheet-changes.component';

/**
 * Left rail for the RILA Rate Sheet (/rila-rate-sheet). Sheet tiles + Scope +
 * Filters form (with Type / Index picker / strategy / downside-protection
 * controls projected in as per-type extras).
 */
@Component({
  selector: 'app-rila-rate-sheet-rail',
  standalone: true,
  imports: [CommonModule, FormsModule, RateSheetTilesComponent, RateSheetFiltersComponent, RateSheetChangesComponent],
  template: `
    <div class="rail-content">
      <app-rate-sheet-tiles />

      <!-- Scope -->
      <div class="rgrp">
        <div class="rgrp-h">Scope</div>
        <div class="knob">
          <span class="klabel">Products</span>
          <div class="seg"
               [class.seg-locked]="shelfState() === 'locked'"
               [class.seg-disabled]="shelfState() === 'disabled'"
               [attr.title]="shelfState() === 'locked' ? 'Your account is restricted to shelf products'
                             : shelfState() === 'disabled' ? 'Select a user via Admin to enable'
                             : 'Toggle between all products and your shelf'">
            <span class="seg-opt" [class.on]="shelfState() === 'off'" (click)="onShelfClick()">All</span>
            <span class="seg-opt" [class.on]="shelfState() === 'on' || shelfState() === 'locked'"
                  [class.forced]="shelfState() === 'locked'" (click)="onShelfClick()">My Shelf</span>
          </div>
        </div>
        <app-rate-sheet-changes [state]="state" />
      </div>

      <!-- Filters (+ Type / Index / Strategy / Downside extras) -->
      <app-rate-sheet-filters [state]="state" termLabel="Term (yr)">
        <div class="rsx">
          <div class="knob">
            <span class="klabel">Type</span>
            <div class="seg">
              <span class="seg-opt" [class.on]="state.sourceType() === ''" (click)="state.sourceType.set('')">All</span>
              <span class="seg-opt" [class.on]="state.sourceType() === 'rila'" (click)="state.sourceType.set('rila')">RILA</span>
              <span class="seg-opt" [class.on]="state.sourceType() === 'hybrid'" (click)="state.sourceType.set('hybrid')">Hybrid</span>
            </div>
          </div>
          <div class="rsx-label">Indexes</div>
          <button #idxTrigger type="button" class="idx-trigger" (click)="openIndexPicker(idxTrigger)">
            <span class="idx-summary" [class.muted]="state.selectedIndexes().length === 0">{{ indexSummary() }}</span>
            <span class="idx-edit">✎</span>
          </button>
          <div class="knob" style="margin-top:10px">
            <span class="klabel">Strategy</span>
            <div class="seg">
              <span class="seg-opt" [class.on]="state.strategyView() === 'capped'" (click)="state.strategyView.set('capped')">Capped</span>
              <span class="seg-opt" [class.on]="state.strategyView() === 'uncapped'" (click)="state.strategyView.set('uncapped')">Uncap.</span>
              <span class="seg-opt" [class.on]="state.strategyView() === 'all'" (click)="state.strategyView.set('all')">All</span>
            </div>
          </div>
          <div class="knob">
            <span class="klabel">Downside</span>
            <div class="seg">
              <span class="seg-opt" [class.on]="state.protectionType() === ''" (click)="onProtType('')">Any</span>
              <span class="seg-opt" [class.on]="state.protectionType() === 'buffer'" (click)="onProtType('buffer')">Buffer</span>
              <span class="seg-opt" [class.on]="state.protectionType() === 'floor'" (click)="onProtType('floor')">Floor</span>
            </div>
          </div>
          <div class="knob">
            <span class="klabel">{{ protLevelLabel() }} ≥</span>
            <select class="ctl" [ngModel]="state.protectionMin()" (ngModelChange)="state.protectionMin.set($event)">
              <option [ngValue]="null">Any level</option>
              @for (lvl of protLevels(); track lvl) { <option [ngValue]="lvl">{{ lvl }}%</option> }
            </select>
          </div>
        </div>
      </app-rate-sheet-filters>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; }
    .rail-content { display: flex; flex-direction: column; }
    .rgrp { border-bottom: 1px solid var(--border-light); padding: 10px 14px 12px; }
    .rgrp-h { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--muted); margin-bottom: 9px; }
    .knob { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 8px 0; }
    .klabel { font-size: 12px; color: var(--text2); font-weight: 600; }
    .ctl { font-family: var(--sans); font-size: 12px; border: 1px solid var(--border); border-radius: 6px; padding: 4px 6px; background: #fff; color: var(--text); min-width: 116px; max-width: 132px; }
    .seg { display: inline-flex; border: 1px solid var(--border); border-radius: 7px; overflow: hidden; }
    .seg-opt { padding: 5px 11px; font-size: 12px; font-weight: 600; color: var(--muted); cursor: pointer; }
    .seg-opt.on { background: var(--navy); color: #fff; }

    .rsx { margin-top: 10px; padding-top: 8px; border-top: 1px dashed var(--border-light); }
    .rsx-label { font-size: 10.5px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; margin: 8px 0 4px; }
    .idx-trigger { display: flex; align-items: center; gap: 8px; width: 100%; border: 1px solid var(--border); border-radius: 6px; background: #fff; padding: 6px 9px; cursor: pointer; text-align: left; }
    .idx-trigger:hover { border-color: var(--blue); }
    .idx-summary { flex: 1; min-width: 0; font-size: 12px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .idx-summary.muted { color: var(--muted); font-weight: 500; }
    .idx-edit { color: var(--muted); font-size: 12px; flex-shrink: 0; }
  `],
})
export class RilaRateSheetRailComponent {
  protected readonly state = inject(RilaRateSheetStateService);
  protected readonly sessionCtx = inject(SessionContextService);
  private readonly rsPopover = inject(RsPopoverService);

  protected readonly indexSummary = computed<string>(() => {
    const sel = this.state.selectedIndexes();
    if (sel.length === 0) return 'All indexes';
    if (sel.length === 1) return sel[0];
    return `${sel[0]} +${sel.length - 1}`;
  });

  openIndexPicker(anchorEl: HTMLElement): void {
    this.rsPopover.open(IndexPickerComponent, {
      anchorEl, title: 'Indexes',
      providers: [{ provide: INDEX_PICKER_STATE, useValue: this.state }],
    });
  }

  protected readonly protLevels = computed<number[]>(() => {
    const t = this.state.protectionType();
    if (t === 'buffer') return this.state.availableBuffers();
    if (t === 'floor') return this.state.availableFloors();
    return [...new Set([...this.state.availableBuffers(), ...this.state.availableFloors()])].sort((a, b) => a - b);
  });
  protected readonly protLevelLabel = computed<string>(() => {
    const t = this.state.protectionType();
    return t === 'floor' ? 'Floor' : t === 'buffer' ? 'Buffer' : 'Protection';
  });
  onProtType(t: ProtectionType): void {
    this.state.protectionType.set(t);
    this.state.protectionMin.set(null);
  }

  protected readonly shelfState = computed<'off' | 'on' | 'locked' | 'disabled'>(() => {
    if (!this.sessionCtx.hasSession()) return 'disabled';
    if (this.sessionCtx.shouldForceShelf('RILA')) return 'locked';
    return this.sessionCtx.shelfMode() ? 'on' : 'off';
  });
  onShelfClick(): void {
    const s = this.shelfState();
    if (s === 'locked' || s === 'disabled') return;
    this.sessionCtx.toggleShelfMode();
  }
}
